
import { computed, defineComponent, onBeforeMount, PropType, reactive, toRaw } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// types
import { EmployeeFullT, EmployeeT, UserDictionaryT } from '@/core/types/users.types'
import { CityT } from '@/core/types/cities.types'

// realisations
import { users } from '@/core/realisations/users.realisation'
import { cities } from '@/core/realisations/cities.realisation'

// validation
import useVuelidate from '@vuelidate/core'
import { required, email, requiredIf } from '@vuelidate/validators'

// classes
import { DataState } from '@/core/classes/DataState.class'

// composable
import useNotifications from '@/common/composable/useNotifications'

// constants
import { EMPLOYEE_DICTIONARIES_TYPES } from '@/core/constants/users.constants'
import { PHONE_NUMBER_MASK } from '@/core/constants/masks.constants'
import { useMaska } from '@/common/composable/useMaska'

export default defineComponent({
    components: {
        Modal,
        TheInput,
        TheSelect,
    },
    props: {
        initialValue: {
            type: Object as PropType<EmployeeT>,
            default: null,
        },
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        isDelete: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['create', 'update', 'delete', 'cancel'],
    setup(props, { emit }) {
        const notifications = useNotifications()
        const { unmaskNumbers } = useMaska()

        const state = reactive({
            fullName: null as string | null,
            organizationId: null as number | null,
            departmentId: null as number | null,
            subdivisionId: null as number | null,
            dateOfReceipt: null as string | null,
            dateOfBirth: null as string | null,
            email: null as string | null,
            phoneNumber: null as string | null,
            workPhone: null as string | null,
            cityId: null as number | null,
            jobId: null as number | null,
            statusId: null as number | null,
        })

        const employee_state = reactive({
            is_loading: false,
        })

        const data_state = reactive({
            cities: new DataState<CityT>({}),
            //
            departments: new DataState<UserDictionaryT>({}),
            jobs: new DataState<UserDictionaryT>({}),
            organizations: new DataState<UserDictionaryT>({}),
            statuses: new DataState<UserDictionaryT>({}),
            subdivisions: new DataState<UserDictionaryT>({}),
        })

        let state_form$ = useVuelidate(
            {
                fullName: { required },
                organizationId: { required },
                departmentId: { required },
                subdivisionId: { required },
                dateOfReceipt: { required },
                dateOfBirth: { required },
                phoneNumber: { required },
                cityId: { required },
                jobId: { required },
                statusId: { required },
            },
            state
        )

        // cities

        const loadCities = async (page: number, search?: string) => {
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const data = await cities.getAllList(page, search)
                data_state.cities.list = data.data

                data_state.cities.pagination.total_pages = data.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const loadMoreCities = async (page: number) => {
            if (page > data_state.cities.pagination.total_pages) return
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const response = await cities.getAllList(page)
                data_state.cities.list = data_state.cities.list.concat(response.data)

                data_state.cities.pagination.total_pages = response.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const searchCity = (search_query: string) => {
            // data_state.cities.pagination.current_page
            loadCities(1, search_query)
        }

        const unshiftFindedCity = async (city: CityT) => {
            await loadCities(data_state.cities.pagination.current_page)
            data_state.cities.list.unshift(city)
        }

        // dictionaries

        const getDictionary = (type: number) => {
            switch (type) {
                case 1:
                    return data_state.departments
                case 2:
                    return data_state.jobs
                case 3:
                    return data_state.organizations
                case 4:
                    return data_state.statuses
                case 5:
                    return data_state.subdivisions
                default:
                    return null
            }
        }

        const loadDictionary = async (type: number, description?: string) => {
            let dictionary = getDictionary(type)

            if (!dictionary) return

            try {
                dictionary.is_loading = true

                const response = await users.getDictionary(type, description)
                dictionary.list = response.data

                dictionary.is_failed = false
                dictionary.failure.error_state.is_cancel = false
            } catch (error) {
                dictionary.failure.catchError(error)

                if (!dictionary.failure.error_state.is_cancel) {
                    dictionary.is_failed = true
                }
            } finally {
                if (!dictionary.failure.error_state.is_cancel) {
                    dictionary.is_loading = false
                }
            }
        }

        const searchDictionary = (search_query: string) => {
            loadDictionary(5, search_query)
        }

        //

        const fillInfo = (employee: EmployeeFullT) => {
            state.fullName = employee.fullName
            state.organizationId = employee.organization.id
            state.departmentId = employee.department.id
            state.subdivisionId = employee.subdivision.id
            state.dateOfReceipt = employee.dateOfReceipt
            state.dateOfBirth = employee.dateOfBirth
            state.email = employee.email
            state.phoneNumber = employee.phoneNumber
            state.workPhone = employee.workPhone
            state.cityId = employee.city.id
            state.jobId = employee.job.id
            state.statusId = employee.status.id
        }

        const loadEmployee = async () => {
            try {
                employee_state.is_loading = true

                const response = await users.getEmployee(props.initialValue.id)

                fillInfo(response.data)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении данных работника произошла ошибка',
                })
            } finally {
                employee_state.is_loading = false
            }
        }

        //

        const getPayload = () => {
            const payload = {
                ...state,
                phoneNumber: unmaskNumbers(state.phoneNumber as string),
                workPhone: state.workPhone ? unmaskNumbers(state.workPhone) : null,
            }

            return payload
        }

        const emitCreate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('create', getPayload())
        }

        const emitUpdate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('update', { id: props.initialValue.id, payload: getPayload() })
        }

        const emitDelete = () => {
            emit('delete', props.initialValue.id)
        }

        const emitCancel = () => {
            emit('cancel')
        }

        const emitManager = () => {
            if (props.isCreate) emitCreate()
            if (props.isUpdate) emitUpdate()
            if (props.isDelete) emitDelete()
        }

        //

        const modalTitle = computed(() => {
            if (props.isCreate) return 'Добавление сотрудника'
            if (props.isUpdate) return 'Редактирование сотрудника'
            if (props.isDelete) return 'Удаление сотрудника'
            return 'modal title'
        })

        const modalConfirmButtonText = computed(() => {
            if (props.isCreate) return 'Добавить'
            if (props.isUpdate) return 'Редактировать'
            if (props.isDelete) return 'Удалить'
            return 'Подтвердить'
        })

        const searchDictionaryOne = (description: string) => {
            loadDictionary(1, description)
        }

        const searchDictionaryTwo = (description: string) => {
            loadDictionary(2, description)
        }

        const searchDictionaryThree = (description: string) => {
            loadDictionary(3, description)
        }

        const searchDictionaryFour = (description: string) => {
            loadDictionary(4, description)
        }

        //

        const loadAllDictionaries = () => {
            EMPLOYEE_DICTIONARIES_TYPES.forEach((dictionary) => loadDictionary(dictionary.id))
        }

        onBeforeMount(() => {
            if (props.isDelete) return

            loadCities(1)
            loadAllDictionaries()

            if (props.isUpdate) loadEmployee()
        })

        //

        return {
            state,
            employee_state,
            data_state,
            state_form$,
            //
            emitCancel,
            emitManager,
            //
            loadCities,
            loadMoreCities,
            searchCity,
            unshiftFindedCity,
            //
            loadDictionary,
            searchDictionary,
            searchDictionaryThree,
            searchDictionaryOne,
            searchDictionaryTwo,
            searchDictionaryFour,
            //
            modalTitle,
            modalConfirmButtonText,
            //
            PHONE_NUMBER_MASK,
        }
    },
})
